// Leitor de Voz — ferramenta pessoal do RUAN. Cola um print (OCR) ou texto e ouve em voz
// alta (Francisca), com Play/Pausar/Parar. Nasceu como app separado; virou tela do ERP pra
// entrar atras da autenticacao e ficar so pro RUAN (menu + endpoints travados por login).
// OCR: /api/ler/ocr (Tesseract). Voz: /api/ler/tts (msedge-tts). Ambos so respondem pro RUAN.
import { useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardPaste, Play, Pause, Square, Trash2 } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { api, getToken } from '../../lib/api';

const VELOCIDADES = ['Devagar', 'Normal', 'Rapido'] as const;

// Teto de caracteres por pedido ao /tts. NAO e limite da voz — e do TEMPO: a sintese
// leva ~10s por milheiro e o nginx corta o pedido em 60s sem resposta, entao um texto
// de 9 mil caracteres estourava e a tela dizia "Nao consegui gerar a voz" (30/07/2026).
// Com 1.200 cada parte volta em ~10s, com folga de sobra.
const MAX_CHARS = 1200;

// Quebra em partes que caibam no teto, cortando em fim de paragrafo ou de frase — nunca
// no meio da palavra. Assim a emenda entre uma parte e outra cai onde a voz ja pausaria.
export function partirTexto(t: string, max = MAX_CHARS): string[] {
  const partes: string[] = [];
  let resto = t.trim();
  while (resto.length > max) {
    const janela = resto.slice(0, max);
    let corte = Math.max(
      janela.lastIndexOf('\n'), janela.lastIndexOf('. '),
      janela.lastIndexOf('! '), janela.lastIndexOf('? '), janela.lastIndexOf('; '),
    );
    if (corte < max * 0.5) corte = janela.lastIndexOf(' '); // sem pontuacao util: corta na palavra
    if (corte <= 0) corte = max - 1;                        // palavra unica gigante: corta seco
    partes.push(resto.slice(0, corte + 1).trim());
    resto = resto.slice(corte + 1).trim();
  }
  if (resto) partes.push(resto);
  return partes;
}

export function Leitor() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [texto, setTexto] = useState('');
  const [velocidade, setVelocidade] = useState<string>('Normal');
  const [status, setStatus] = useState('');
  const [gerando, setGerando] = useState(false);
  const [podePausar, setPodePausar] = useState(false);
  const [rotuloPausar, setRotuloPausar] = useState<'Pausar' | 'Retomar'>('Pausar');

  // Leitura em partes: a sessao invalida o que estava em voo quando o Ruan aperta
  // Parar ou Ler de novo (resposta atrasada de uma leitura antiga nao pode voltar a tocar).
  const sessaoRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const partesRef = useRef<string[]>([]);
  const indiceRef = useRef(0);
  const urlsRef = useRef<Array<Promise<string | null> | undefined>>([]);

  // So o RUAN — a rota nem deveria chegar aqui pra outro login, mas trava mesmo assim.
  if (usuario && usuario.login !== 'RUAN') return <Navigate to="/" replace />;

  function anexa(t: string): boolean {
    const novo = (t || '').trim();
    if (!novo) return false;
    setTexto((prev) => (prev.trim() ? prev.trim() + ' ' + novo : novo));
    return true;
  }

  async function ocrDataURL(dataURL: string) {
    setStatus('Lendo a imagem (OCR)...');
    try {
      const r = await api.post<{ texto: string }>('/api/ler/ocr', { image: dataURL });
      setStatus(anexa(r.texto) ? 'Trecho adicionado. Cole mais um print, ou clique em Ler.' : 'Nao achei texto nessa imagem.');
    } catch {
      setStatus('Falha no OCR.');
    }
  }

  function blobParaOcr(blob: Blob) {
    const r = new FileReader();
    r.onload = () => ocrDataURL(String(r.result));
    r.readAsDataURL(blob);
  }

  // Botao Colar print — le o clipboard sob demanda (nao depende do foco).
  async function colar() {
    try {
      const itens = await navigator.clipboard.read();
      for (const item of itens) {
        const tImg = item.types.find((t) => t.startsWith('image/'));
        if (tImg) { blobParaOcr(await item.getType(tImg)); return; }
      }
      const t = await navigator.clipboard.readText();
      setStatus(anexa(t) ? 'Texto adicionado.' : 'Nada de imagem ou texto pra colar.');
    } catch {
      setStatus('Nao consegui ler o clipboard — printe/copie e clique em Colar logo em seguida.');
    }
  }

  // Ctrl+V em imagem tambem funciona (atalho).
  function onPaste(e: React.ClipboardEvent) {
    for (const it of e.clipboardData.items) {
      if (it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) { e.preventDefault(); blobParaOcr(f); return; }
      }
    }
  }

  // Gera UMA parte (memoizada: a mesma parte nunca e pedida duas vezes, porque a
  // proxima ja e adiantada enquanto a atual toca).
  function gerarParte(i: number, sessao: number): Promise<string | null> {
    const emCache = urlsRef.current[i];
    if (emCache) return emCache;
    const p = (async () => {
      const res = await fetch('/api/ler/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ texto: partesRef.current[i], velocidade }),
        signal: abortRef.current?.signal,
      });
      if (!res.ok) return null;
      if (sessao !== sessaoRef.current) return null;
      return URL.createObjectURL(await res.blob());
    })().catch(() => null);
    urlsRef.current[i] = p;
    return p;
  }

  async function tocarParte(i: number, sessao: number) {
    const el = audioRef.current;
    if (!el || sessao !== sessaoRef.current) return;
    const total = partesRef.current.length;
    if (!urlsRef.current[i]) setStatus(total > 1 ? `Gerando a voz (parte ${i + 1} de ${total})...` : 'Gerando a voz...');
    const url = await gerarParte(i, sessao);
    if (sessao !== sessaoRef.current) return;
    if (!url) { setStatus('Nao consegui gerar a voz.'); setGerando(false); setPodePausar(false); return; }
    if (i + 1 < total) void gerarParte(i + 1, sessao); // adianta a proxima enquanto esta toca
    indiceRef.current = i;
    el.src = url;
    try { await el.play(); } catch { /* troca de src cancela o play anterior */ }
    if (sessao !== sessaoRef.current) return;
    setGerando(false);
    setPodePausar(true);
    setRotuloPausar('Pausar');
    setStatus(total > 1 ? `Tocando — parte ${i + 1} de ${total}.` : 'Tocando.');
  }

  async function ler() {
    const t = texto.trim();
    if (!t) { setStatus('Nada pra ler.'); return; }
    encerrarSessao();
    const sessao = ++sessaoRef.current;
    abortRef.current = new AbortController();
    partesRef.current = partirTexto(t);
    indiceRef.current = 0;
    urlsRef.current = [];
    setGerando(true);
    setStatus('Gerando a voz...');
    await tocarParte(0, sessao);
  }

  // Invalida a leitura em curso: para o audio, aborta o que esta em voo e devolve
  // os object URLs ja criados (senao cada leitura longa deixa alguns MB pendurados).
  function encerrarSessao() {
    sessaoRef.current++;
    abortRef.current?.abort();
    abortRef.current = null;
    const el = audioRef.current;
    if (el) { el.pause(); el.removeAttribute('src'); }
    for (const p of urlsRef.current) {
      void p?.then((u) => { if (u) URL.revokeObjectURL(u); });
    }
    urlsRef.current = [];
    partesRef.current = [];
  }

  // Fim de uma parte: emenda a proxima na hora (ela ja foi adiantada) ou encerra.
  function aoTerminarParte() {
    const prox = indiceRef.current + 1;
    if (prox < partesRef.current.length) { void tocarParte(prox, sessaoRef.current); return; }
    setPodePausar(false);
    setRotuloPausar('Pausar');
    setStatus('Fim.');
  }

  function pausarRetomar() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { void el.play(); setRotuloPausar('Pausar'); setStatus('Tocando.'); }
    else { el.pause(); setRotuloPausar('Retomar'); setStatus('Pausado.'); }
  }

  function parar() {
    encerrarSessao();
    setGerando(false);
    setPodePausar(false);
    setRotuloPausar('Pausar');
    setStatus('Parado.');
  }

  function limpar() {
    setTexto('');
    setStatus('Texto limpo.');
  }

  const btn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    border: '1px solid var(--border)', background: 'var(--panel-bg)',
    color: 'var(--page-fg)', cursor: 'pointer',
  };
  const btnDisabled: React.CSSProperties = { opacity: 0.5, cursor: 'default' };

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn-icon"
            title="Voltar"
            aria-label="Voltar"
            style={{ marginRight: 4 }}
          >
            <ArrowLeft size={20} />
          </button>
          Leitor de Voz
        </h2>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0, marginBottom: 12 }}>
        Cole um print (Ctrl+V ou botao Colar) ou texto e clique em Ler. Pode editar antes de ouvir.
      </p>

      <textarea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onPaste={onPaste}
        placeholder="O texto a ser lido aparece aqui..."
        style={{ width: '100%', minHeight: '52vh', padding: 12, borderRadius: 10, fontSize: 15, lineHeight: 1.45, resize: 'vertical' }}
      />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
        <button type="button" onClick={colar} style={btn}><ClipboardPaste size={16} /> Colar print</button>
        <button type="button" onClick={ler} disabled={gerando} className="btn-primario" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Play size={16} /> {gerando ? 'Gerando...' : 'Ler'}
        </button>
        <button type="button" onClick={pausarRetomar} disabled={!podePausar} style={{ ...btn, ...(podePausar ? {} : btnDisabled) }}>
          <Pause size={16} /> {rotuloPausar}
        </button>
        <button type="button" onClick={parar} disabled={!podePausar} style={{ ...btn, ...(podePausar ? {} : btnDisabled) }}>
          <Square size={16} /> Parar
        </button>
        <button type="button" onClick={limpar} style={btn}><Trash2 size={16} /> Limpar</button>
        <span style={{ flex: 1 }} />
        <label style={{ color: 'var(--text-muted)', fontSize: 14 }}>Velocidade:</label>
        <select value={velocidade} onChange={(e) => setVelocidade(e.target.value)} style={{ padding: '8px 10px', borderRadius: 8 }}>
          {VELOCIDADES.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div style={{ marginTop: 12, color: 'var(--primary)', fontSize: 14, minHeight: 20 }}>{status}</div>

      <audio ref={audioRef} onEnded={aoTerminarParte} />
    </div>
  );
}
